import React, { useEffect, useState } from 'react';
import { Checkbox } from '@bblabs/module-library/molecules';
import { clearNotification } from '../abstractions/notifications';
import { getSettingsService, AppSettings } from '../services/SettingsService';
import { isConfiguredForAutoTracking, isNativeSend, trackByDefault } from '../lib/host';
import { getBooleanCustomProperty, getComposeItem, setBooleanCustomProperty } from '../abstractions/item';
import { isViewInReadMode } from '../abstractions/office';
import { SendAndTrack, Props as SendAndTrackProps } from './SendAndTrack';
import { AutoTrackingGlyph } from './AutoTrackingGlyph';

import './TrackingControls.scss';

type Props = SendAndTrackProps;

/**
 * Composite component that shows either the Send & Track button or the Tracking Toggle
 */
export function TrackingControls({ onSend, showSendInfoOverlay }: Props) {
  const settings = getSettingsService('bombbomb');
  const [loading, setLoading] = useState(true);
  const [trackAll, setTrackAll] = useState(settings.get('trackAll') ?? trackByDefault());
  const [trackThisEmail, setTrackThisEmail] = useState<boolean | null>(null);

  useEffect(() => {
    function onSettingsSaved(settings: AppSettings) {
      setTrackAll(settings.trackAll);
    }

    settings.addListener('saved', onSettingsSaved);

    return () => {
      settings.removeListener('saved', onSettingsSaved);
    };
  }, [settings]);

  useEffect(() => {
    if (isNativeSend()) {
      const item = getComposeItem();
      getBooleanCustomProperty(item, 'TRACK_EMAIL').then((canTrack) => {
        setTrackThisEmail(canTrack);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  async function handleChange(trackEmail: boolean) {
    setTrackThisEmail(trackEmail);
    const item = getComposeItem();
    await setBooleanCustomProperty(item, 'TRACK_EMAIL', trackEmail);
    await setBooleanCustomProperty(item, 'SKIP_TRACKING_ERROR', null);
    await clearNotification();
  }

  if (isViewInReadMode()) {
    return null;
  }

  return (
    <div className="tracking-controls">
      {!loading && isNativeSend() && isConfiguredForAutoTracking() && <AutoTrackingGlyph />}
      {!loading && isNativeSend() && !isConfiguredForAutoTracking() && (
        <label title="Turn on or off tracking for this email">
          Track Email
          <Checkbox
            defaultChecked={trackThisEmail ?? trackAll}
            type="square"
            name="track"
            onChange={handleChange}
          />
        </label>
      )}
      {!loading && !isNativeSend() && !isConfiguredForAutoTracking() && (
        <SendAndTrack onSend={onSend} showSendInfoOverlay={showSendInfoOverlay} />
      )}
    </div>
  );
}
